import { normalizeProgramLens, type ProviderProgramContext } from '@/lib/navigation/provider-type';

type RememberableContext = Pick<
  ProviderProgramContext,
  | 'activeProgramFamily'
  | 'authorityRevision'
  | 'selectionInvalid'
  | 'selectionRequired'
  | 'selectionSource'
  | 'workspace'
>;

/**
 * What the shell should ask the server to remember, or null when there is nothing to remember.
 *
 * Only a programme the server itself resolved FROM THE URL, and accepted, qualifies: a choice
 * made with the switcher is already stored, an automatic single-programme resolution needs no
 * storing, and an invalid or still-required selection must never be persisted from the client.
 *
 * `alreadyStored` comes from the server (the layout compares the lens cookie with the context it
 * just resolved). The browser keeps no memory of its own: a marker in the tab would go stale the
 * moment the stored lens is moved by the switcher or by another tab.
 */
export function programLensToRemember(
  context: RememberableContext,
  tenantId: string | null | undefined,
  alreadyStored: boolean,
): { key: string; field: 'workspace' | 'programFamily'; value: string } | null {
  if (
    alreadyStored ||
    !tenantId ||
    context.selectionSource !== 'URL' ||
    context.selectionInvalid ||
    context.selectionRequired
  )
    return null;
  const workspace = context.workspace ?? null;
  const family = context.activeProgramFamily;
  if (workspace)
    return {
      key: [tenantId, `workspace:${workspace}`, context.authorityRevision].join('|'),
      field: 'workspace',
      value: workspace,
    };
  if (family)
    return {
      key: [tenantId, family, context.authorityRevision].join('|'),
      field: 'programFamily',
      value: family,
    };
  return null;
}

/**
 * Whether the shell should send a remember request now, given the choice it last asked about.
 *
 * - Nothing to remember (not a URL choice, or the server reports it stored): forget the last
 *   attempt, so the same choice can be remembered again later if the stored lens is moved away
 *   and the person comes back to it (remember A, switch to B, reopen A by link).
 * - The same choice as the last attempt and still not stored: the server refused it or the
 *   request is in flight. Do not ask again.
 * - A different choice: ask once.
 */
export function decideRememberAttempt(
  lastAttemptedKey: string | null,
  lens: { key: string } | null,
): { lastAttemptedKey: string | null; attempt: boolean } {
  if (!lens) return { lastAttemptedKey: null, attempt: false };
  if (lastAttemptedKey === lens.key) return { lastAttemptedKey, attempt: false };
  return { lastAttemptedKey: lens.key, attempt: true };
}

/**
 * What the shell should do about `?program=` in the address bar after a navigation.
 *
 * The dashboard layout is not re-rendered on in-app navigation, so the shell can be holding the
 * programme of the page it was first rendered for while the URL already names another one.
 *
 * - `none`: nothing to do (no single programme in play, or the URL already matches).
 * - `refresh`: the URL names a DIFFERENT programme this organisation offers. That is the
 *   person's choice (an in-app link to another programme). Never overwrite it: re-render the
 *   route so the server resolves the programme from the URL; the shell then follows it and
 *   the remember path stores it.
 * - `write`: the URL has no programme, or one this organisation does not offer. Put the shell's
 *   programme in the address bar so the URL is shareable. Cosmetic only.
 */
export function addressBarProgramAction(
  urlProgram: string | null,
  context: Pick<ProviderProgramContext, 'activeProgramFamily' | 'workspace' | 'lensOptions'>,
): 'none' | 'refresh' | 'write' {
  if (!context.activeProgramFamily || context.workspace) return 'none';
  if (urlProgram === context.activeProgramFamily) return 'none';
  const requested = normalizeProgramLens(urlProgram);
  if (requested === context.activeProgramFamily) return 'none';
  if (requested && context.lensOptions.some((option) => option.family === requested))
    return 'refresh';
  return 'write';
}
